import React, { useState, useRef, useEffect } from "react";
import { usePersistedState } from "hooks/utility/usePersistedState";
import { useNavigate } from "react-router-dom";
import {
  Send,
  Sparkles,
  BrainCircuit,
  BarChart3,
  TrendingUp,
  FileText,
  User,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  PieChart,
  Activity,
  LayoutList,
  Download,
} from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { fetchClient } from "../../API/fetchClient";
import type { User as BackendUser } from "../../types/user";
import "./YameeraAnalystPage.css";

// ─── Types ─────────────────────────────────────────────────────────────────

type OutputType = "analyse" | "graphique" | "tableau" | "rapport";
type ChartType = "bar" | "pie" | "scatter" | "line";

interface RSETable {
  name: string;
  description: string;
  columns: string[];
  data: Record<string, any>[];
}

interface RSEChart {
  name: string;
  description: string;
  file_name: string;
  html: string;
}

interface RSEAnalystResult {
  output_type: string;
  summary: string;
  tables: RSETable[];
  charts: RSEChart[];
  pdf_html: string | null;
  warnings: string[];
  meta: {
    metric: string;
    dimension: string;
    elapsed_s: number;
    rows_analyzed: number;
    used_ai: boolean;
    resolved_type: string;
    dimension_filters: Record<string, string[]>;
    campaigns_filter: string[];
  };
}

interface Message {
  id: number;
  role: "user" | "assistant";
  content: string;
  timestamp: string; // ISO string — survives JSON serialization in localStorage
  result?: RSEAnalystResult;
  isError?: boolean;
}

interface YameeraAnalystPageProps {
  user: BackendUser;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const OUTPUT_TYPES: { value: OutputType; label: string; icon: React.ReactNode }[] = [
  { value: "analyse", label: "Analyse", icon: <BrainCircuit size={14} /> },
  { value: "graphique", label: "Graphique", icon: <BarChart3 size={14} /> },
  { value: "tableau", label: "Tableau", icon: <LayoutList size={14} /> },
  { value: "rapport", label: "Rapport", icon: <FileText size={14} /> },
];

const CHART_TYPES: { value: ChartType; label: string; icon: React.ReactNode }[] = [
  { value: "bar", label: "Barres", icon: <BarChart3 size={13} /> },
  { value: "pie", label: "Camembert", icon: <PieChart size={13} /> },
  { value: "scatter", label: "Nuage", icon: <Activity size={13} /> },
  { value: "line", label: "Courbe", icon: <TrendingUp size={13} /> },
];

const SUGGESTIONS = [
  {
    icon: <BarChart3 size={16} />,
    label: "Distribution de la consommation DH par région",
    type: "graphique" as OutputType,
    chartType: "bar" as ChartType,
    short: "Conso par région",
  },
  {
    icon: <TrendingUp size={16} />,
    label: "Évolution de la consommation TEP par campagne",
    type: "graphique" as OutputType,
    chartType: "line" as ChartType,
    short: "Évolution TEP",
  },
  {
    icon: <LayoutList size={16} />,
    label: "Top 10 sites par consommation d'énergie",
    type: "tableau" as OutputType,
    chartType: undefined,
    short: "Top sites",
  },
  {
    icon: <FileText size={16} />,
    label: "Rapport bilan énergétique complet",
    type: "rapport" as OutputType,
    chartType: undefined,
    short: "Rapport bilan",
  },
];

// ─── ChartBlock sub-component (needs ref for PNG download) ─────────────────

const ChartBlock: React.FC<{ chart: RSEChart }> = ({ chart }) => {
  const outputRef = useRef<HTMLDivElement>(null);

  const downloadPng = () => {
    const container = outputRef.current;
    if (!container) return;
    const svg = container.querySelector("svg");
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const w = rect.width || 800;
    const h = rect.height || 500;
    const scale = 2;

    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));

    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    const canvas = document.createElement("canvas");
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext("2d")!;

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${chart.name || "graphique"}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
    };
    img.src = url;
  };

  return (
    <div className="ya-chart-block">
      <div className="ya-chart-block-header">
        <div>
          <p className="ya-chart-label">{chart.name}</p>
          {chart.description && (
            <p className="ya-chart-desc">{chart.description}</p>
          )}
        </div>
        <button
          className="ya-chart-download-btn"
          onClick={downloadPng}
          title="Télécharger en PNG"
        >
          <Download size={13} />
          PNG
        </button>
      </div>
      <div
        ref={outputRef}
        className="ya-chart-output"
        dangerouslySetInnerHTML={{ __html: chart.html }}
      />
    </div>
  );
};

// ─── PDF helpers (module-level pure functions) ──────────────────────────────

// Inject a script that overrides window.print → download HTML (fallback)
const patchPdfHtml = (html: string): string => {
  const script = `<script>
(function(){
  window.print = function(){
    var blob = new Blob([document.documentElement.outerHTML], {type:'text/html;charset=utf-8'});
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = 'rapport-rse-analyst.html';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
})();
</script>`;
  return html.includes("</head>")
    ? html.replace("</head>", script + "</head>")
    : script + html;
};

// Render the HTML in a hidden iframe, capture with html2canvas, save as PDF
const downloadReportAsPdf = async (html: string): Promise<void> => {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);

  // Create a hidden A4-sized iframe
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, {
    position: "fixed",
    top: "-99999px",
    left: "-99999px",
    width: "794px",
    height: "1123px",
    border: "none",
    visibility: "hidden",
  });
  document.body.appendChild(iframe);

  await new Promise<void>((resolve) => {
    iframe.onload = () => resolve();
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();
  });

  // Extra time for fonts/images inside the iframe
  await new Promise((r) => setTimeout(r, 900));

  const iframeDoc = iframe.contentDocument!;
  const A4_W = 210;
  const A4_H = 297;
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

  try {
    // Try to find explicit page containers first
    const pageEls = Array.from(
      iframeDoc.querySelectorAll<HTMLElement>(
        '[class*="page"]:not([class*="topbar"]):not([class*="nav"])'
      )
    ).filter((el) => el.offsetHeight > 100);

    if (pageEls.length > 0) {
      for (let i = 0; i < pageEls.length; i++) {
        const canvas = await html2canvas(pageEls[i], {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: 794,
          height: 1123,
        });
        const img = canvas.toDataURL("image/jpeg", 0.93);
        if (i > 0) pdf.addPage();
        pdf.addImage(img, "JPEG", 0, 0, A4_W, A4_H);
      }
    } else {
      // Fallback: capture the full body and paginate
      const canvas = await html2canvas(iframeDoc.body, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: 794,
      });
      const img = canvas.toDataURL("image/jpeg", 0.93);
      const imgW = A4_W;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let pos = 0;
      pdf.addImage(img, "JPEG", 0, pos, imgW, imgH);
      heightLeft -= A4_H;
      while (heightLeft > 0) {
        pos -= A4_H;
        pdf.addPage();
        pdf.addImage(img, "JPEG", 0, pos, imgW, imgH);
        heightLeft -= A4_H;
      }
    }

    pdf.save("rapport-rse-analyst.pdf");
  } finally {
    document.body.removeChild(iframe);
  }
};

// ─── PdfBlock sub-component ─────────────────────────────────────────────────

const PdfBlock: React.FC<{ html: string }> = ({ html }) => {
  const [generating, setGenerating] = useState(false);

  const handleDownload = async () => {
    setGenerating(true);
    try {
      await downloadReportAsPdf(html);
    } catch (e) {
      console.error("PDF generation failed:", e);
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenTab = () => {
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(patchPdfHtml(html));
      w.document.close();
    }
  };

  return (
    <div className="ya-pdf-block">
      <div className="ya-pdf-header">
        <span className="ya-pdf-label">Rapport généré</span>
        <div className="ya-pdf-actions">
          <button
            className="ya-pdf-dl-btn"
            onClick={handleDownload}
            disabled={generating}
          >
            <Download size={13} />
            {generating ? "Génération…" : "Télécharger PDF"}
          </button>
          <button className="ya-pdf-open-btn" onClick={handleOpenTab}>
            <FileText size={13} />
            Ouvrir
          </button>
        </div>
      </div>
      <iframe
        className="ya-pdf-iframe"
        srcDoc={patchPdfHtml(html)}
        title="Rapport RSE"
      />
    </div>
  );
};

// ─── Table download helpers ─────────────────────────────────────────────────

const downloadTableAsCsv = (table: RSETable) => {
  const escape = (val: any) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    table.columns.join(","),
    ...table.data.map((row) => table.columns.map((col) => escape(row[col])).join(",")),
  ];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${table.name || "tableau"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadTableAsExcel = (table: RSETable) => {
  const tr = (cells: string[], tag: "th" | "td") =>
    `<tr>${cells.map((c) => `<${tag}>${c}</${tag}>`).join("")}</tr>`;
  const htmlTable = [
    "<table>",
    tr(table.columns, "th"),
    ...table.data.map((row) =>
      tr(
        table.columns.map((col) => {
          const v = row[col];
          return v === null || v === undefined ? "" : String(v);
        }),
        "td"
      )
    ),
    "</table>",
  ].join("");
  const blob = new Blob([htmlTable], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${table.name || "tableau"}.xls`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Component ──────────────────────────────────────────────────────────────

const YameeraAnalystPage: React.FC<YameeraAnalystPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = usePersistedState<Message[]>("yameera-chat-history", []);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [outputType, setOutputType] = useState<OutputType>("graphique");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [anneeMin, setAnneeMin] = useState("");
  const [anneeMax, setAnneeMax] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const sendMessage = async (
    text: string,
    overrideType?: OutputType,
    overrideChart?: ChartType
  ) => {
    if (!text.trim() || isLoading) return;

    const resolvedType = overrideType ?? outputType;
    const resolvedChart = overrideChart ?? chartType;

    const userMsg: Message = {
      id: Date.now(),
      role: "user",
      content: text.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg].slice(-50));
    setInput("");
    setIsLoading(true);

    const body: Record<string, any> = {
      type: resolvedType,
      query: text.trim(),
    };
    if (resolvedType === "graphique") body.chart_type = resolvedChart;
    const min = parseInt(anneeMin);
    const max = parseInt(anneeMax);
    if (!isNaN(min)) body.annee_min = min;
    if (!isNaN(max)) body.annee_max = max;

    const response = await fetchClient<RSEAnalystResult>("/core/rse-analyst/", {
      method: "POST",
      body,
    });

    setIsLoading(false);

    if (response.error || !response.data) {
      const detail =
        response.error?.detail ??
        response.error?.message ??
        "Une erreur s'est produite lors de l'analyse.";
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant" as const,
          content: detail,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ].slice(-50));
      return;
    }

    const data = response.data;
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 1,
        role: "assistant" as const,
        content: data.summary ?? "",
        timestamp: new Date().toISOString(),
        result: data,
      },
    ].slice(-50));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  // ─── Result renderer ─────────────────────────────────────────────────────

  const renderResult = (result: RSEAnalystResult) => {
    const { charts, tables, summary, pdf_html, warnings, meta } = result;

    return (
      <div className="ya-result">
        {warnings.length > 0 && (
          <div className="ya-warnings">
            {warnings.map((w, i) => (
              <p key={i} className="ya-warning-item">
                <AlertCircle size={12} /> {w}
              </p>
            ))}
          </div>
        )}

        {/* Charts */}
        {charts.map((chart, i) => (
          <ChartBlock key={i} chart={chart} />
        ))}

        {/* Tables */}
        {tables.map((table, ti) => (
          <div key={ti} className="ya-result-table-block">
            <div className="ya-chart-block-header">
              <p className="ya-chart-label">{table.description || table.name}</p>
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  className="ya-chart-download-btn"
                  onClick={() => downloadTableAsCsv(table)}
                  title="Télécharger en CSV"
                >
                  <Download size={13} />
                  CSV
                </button>
                <button
                  className="ya-chart-download-btn"
                  onClick={() => downloadTableAsExcel(table)}
                  title="Télécharger en Excel"
                >
                  <Download size={13} />
                  Excel
                </button>
              </div>
            </div>
            <div className="ya-table-wrap">
              <table className="ya-table">
                <thead>
                  <tr>
                    {table.columns.map((col, ci) => (
                      <th key={ci}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.data.map((row, ri) => (
                    <tr key={ri}>
                      {table.columns.map((col, ci) => {
                        const val = row[col];
                        return (
                          <td key={ci}>
                            {val === null || val === undefined
                              ? "—"
                              : typeof val === "number"
                              ? val.toLocaleString("fr-FR", {
                                  maximumFractionDigits: 2,
                                })
                              : String(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Summary text (text/analyse mode) */}
        {summary && charts.length === 0 && tables.length === 0 && !pdf_html && (
          <p className="ya-summary-text">{summary}</p>
        )}

        {/* PDF Report */}
        {pdf_html && <PdfBlock html={pdf_html} />}

        {/* Meta chips */}
        {meta && (
          <div className="ya-meta">
            {meta.metric && <span className="ya-meta-chip">{meta.metric}</span>}
            {meta.dimension && (
              <span className="ya-meta-chip">{meta.dimension}</span>
            )}
            {meta.rows_analyzed > 0 && (
              <span className="ya-meta-chip">
                {meta.rows_analyzed} lignes analysées
              </span>
            )}
            {meta.elapsed_s > 0 && (
              <span className="ya-meta-chip">{meta.elapsed_s.toFixed(1)}s</span>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const isEmpty = messages.length === 0;

  return (
    <div className="ya-page">
      <Topbar
        title="Yameera Analyst"
        userName={user.username}
        onLogout={() => navigate("/")}
        user={user}
      />

      <div className="ya-layout">
        <div className="ya-chat-area">
          {/* Empty state */}
          {isEmpty ? (
            <div className="ya-empty">
              <div className="ya-empty-icon">
                <Sparkles size={32} />
              </div>
              <h2 className="ya-empty-title">Yameera Analyst</h2>
              <p className="ya-empty-desc">
                Pose une question en langage naturel sur tes données énergie.
                Yameera génère automatiquement graphiques, tableaux et rapports.
              </p>
              <div className="ya-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="ya-suggestion"
                    onClick={() => {
                      if (s.type) setOutputType(s.type);
                      if (s.chartType) setChartType(s.chartType);
                      sendMessage(s.label, s.type, s.chartType);
                    }}
                  >
                    <span className="ya-suggestion-icon">{s.icon}</span>
                    <span className="ya-suggestion-text">{s.label}</span>
                    <ChevronRight size={14} className="ya-suggestion-arrow" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Messages */
            <div className="ya-messages">
              <div className="ya-messages-topbar">
                <span className="ya-messages-label">
                  <BrainCircuit size={14} />
                  Yameera Analyst
                </span>
                <button className="ya-reset-btn" onClick={() => setMessages([])}>
                  <RefreshCw size={13} />
                  Nouvelle analyse
                </button>
              </div>

              {messages.map((msg) => (
                <div key={msg.id} className={`ya-msg ya-msg--${msg.role}`}>
                  {msg.role === "assistant" && (
                    <div className="ya-msg-avatar ya-msg-avatar--ai">
                      <BrainCircuit size={16} />
                    </div>
                  )}
                  <div className={`ya-msg-bubble-wrap${msg.result ? " ya-msg-bubble-wrap--wide" : ""}`}>
                    <div
                      className={`ya-msg-bubble ya-msg-bubble--${msg.role}${
                        msg.isError ? " ya-msg-bubble--error" : ""
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        msg.result ? (
                          renderResult(msg.result)
                        ) : (
                          <div className="ya-error-content">
                            <AlertCircle size={15} className="ya-error-icon" />
                            <p className="ya-msg-text">{msg.content}</p>
                          </div>
                        )
                      ) : (
                        <p className="ya-msg-text">{msg.content}</p>
                      )}
                    </div>
                    <span className="ya-msg-time">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  {msg.role === "user" && (
                    <div className="ya-msg-avatar ya-msg-avatar--user">
                      <User size={14} />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="ya-msg ya-msg--assistant">
                  <div className="ya-msg-avatar ya-msg-avatar--ai">
                    <BrainCircuit size={16} />
                  </div>
                  <div className="ya-typing-bubble">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* ─── Input Area ─────────────────────────────────────── */}
          <div className="ya-input-area">
            {/* Persistent suggestion chips */}
            <div className="ya-quick-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  className="ya-quick-chip"
                  onClick={() => {
                    if (s.type) setOutputType(s.type);
                    if (s.chartType) setChartType(s.chartType);
                    sendMessage(s.label, s.type, s.chartType);
                  }}
                >
                  {s.icon}
                  {s.short}
                </button>
              ))}
            </div>

            {/* Controls strip */}
            <div className="ya-controls">
              <div className="ya-type-selector">
                {OUTPUT_TYPES.map((t) => (
                  <button
                    key={t.value}
                    className={`ya-type-btn${
                      outputType === t.value ? " ya-type-btn--active" : ""
                    }`}
                    onClick={() => setOutputType(t.value)}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>

              {outputType === "graphique" && (
                <div className="ya-chart-selector">
                  {CHART_TYPES.map((c) => (
                    <button
                      key={c.value}
                      className={`ya-chart-btn${
                        chartType === c.value ? " ya-chart-btn--active" : ""
                      }`}
                      onClick={() => setChartType(c.value)}
                    >
                      {c.icon}
                      {c.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="ya-year-filters">
                <input
                  type="number"
                  className="ya-year-input"
                  placeholder="Année min"
                  value={anneeMin}
                  onChange={(e) => setAnneeMin(e.target.value)}
                  min={2000}
                  max={2030}
                />
                <span className="ya-year-sep">–</span>
                <input
                  type="number"
                  className="ya-year-input"
                  placeholder="Année max"
                  value={anneeMax}
                  onChange={(e) => setAnneeMax(e.target.value)}
                  min={2000}
                  max={2030}
                />
              </div>
            </div>

            {/* Text input */}
            <div className="ya-input-box">
              <textarea
                className="ya-textarea"
                placeholder="Demande une analyse, un graphique, un tableau ou un rapport…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                className={`ya-send-btn${
                  input.trim() && !isLoading ? " ya-send-btn--active" : ""
                }`}
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
              >
                <Send size={16} />
              </button>
            </div>
            <p className="ya-input-hint">
              Entrée pour envoyer · Maj+Entrée pour nouvelle ligne
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YameeraAnalystPage;
